// Approval chains — the CLIENT MIRROR of PhoenixCapital.Core/Common/
// ApprovalChainDefinition.cs › ApprovalChainRules. The server is
// authoritative (it re-validates every save and send); this mirror only lets
// the editor and the composer refuse an edit before a round-trip, and it never
// offers a control the server would reject. Messages match the server's.

import type {
  ApprovalChainDefinition,
  ApprovalChainRole,
  ApprovalChainStageDefinition,
} from "@/api/approvalChains";
import { APPROVAL_CHAIN_ROLE_LABEL } from "@/components/Collection/collectionStatusLabels";

export const COUNSEL_PLACEHOLDER = "[Counsel to supply]";
export const COUNSEL_PLACEHOLDER_WARNING =
  "A send is blocked until counsel supplies this wording";
export const MANDATORY_LOCK_HINT = "Mandatory — cannot be removed or moved";
export const MAX_CAPTION_LENGTH = 200;
export const MAX_STATEMENT_LENGTH = 2000;
export const MAX_STAGES = 10;

/** The D2 roles a definition may hold, in picker order. BuyerSideSigner is
 *  never a stage of its own — co-signers ride the Buyer stage. */
export const D2_ROLES: readonly ApprovalChainRole[] = ["Broker", "Seller", "Buyer", "Phoenix"];

export const roleLabel = (role: ApprovalChainRole): string =>
  APPROVAL_CHAIN_ROLE_LABEL[role] ?? role;

const hasPlaceholder = (text: string | null | undefined) =>
  typeof text === "string" && text.includes(COUNSEL_PLACEHOLDER);

/**
 * True when a stage still carries the counsel placeholder. The co-signer
 * caption / statement count only when they would print: on a send, when the
 * Buyer stage has co-signers (the server's HasPlaceholderStage); in the
 * template editor (`coSignerTextAlwaysCounts`), always — any later send that
 * adds a co-signer would be blocked by it.
 */
export function stageHasPlaceholder(
  stage: ApprovalChainStageDefinition,
  coSignerTextAlwaysCounts = false,
): boolean {
  if (hasPlaceholder(stage.caption) || hasPlaceholder(stage.statement)) return true;
  const coSignerCounts =
    coSignerTextAlwaysCounts || (stage.coSignerPortalUserIds?.length ?? 0) > 0;
  return coSignerCounts && (hasPlaceholder(stage.coSignerCaption) || hasPlaceholder(stage.coSignerStatement));
}

/** A stage the baseline holds as mandatory at this position: it cannot be
 *  removed, moved, re-roled or made optional (D3). */
export function isLockedAt(baseline: ApprovalChainDefinition | null | undefined, index: number): boolean {
  return baseline?.stages[index]?.mandatory === true;
}

/** Removing the stage at `index` is allowed only when it is not locked AND no
 *  locked stage sits after it — removal would shift that stage's position. */
export function canRemoveStage(
  stages: readonly ApprovalChainStageDefinition[],
  index: number,
  baseline: ApprovalChainDefinition | null | undefined,
): boolean {
  if (index < 0 || index >= stages.length) return false;
  for (let i = index; i < Math.max(stages.length, baseline?.stages.length ?? 0); i++) {
    if (isLockedAt(baseline, i)) return false;
  }
  return true;
}

/** Moving swaps two neighbours, so neither may be locked. */
export function canMoveStage(
  stages: readonly ApprovalChainStageDefinition[],
  index: number,
  direction: -1 | 1,
  baseline: ApprovalChainDefinition | null | undefined,
): boolean {
  const target = index + direction;
  if (index < 0 || index >= stages.length || target < 0 || target >= stages.length) return false;
  return !isLockedAt(baseline, index) && !isLockedAt(baseline, target);
}

export function moveStage<T>(stages: readonly T[], index: number, direction: -1 | 1): T[] {
  const next = stages.slice();
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** The D2 roles not yet used by any stage (a role appears at most once). */
export function unusedRoles(stages: readonly ApprovalChainStageDefinition[]): ApprovalChainRole[] {
  const used = new Set(stages.map((s) => s.role));
  return D2_ROLES.filter((r) => !used.has(r));
}

/** The mirror of ApprovalChainRules.Validate — empty means valid. */
export function validateChain(
  chain: ApprovalChainDefinition,
  isBorrowerFacing: boolean,
  baseline: ApprovalChainDefinition | null | undefined,
): string[] {
  const errors: string[] = [];
  const stages = chain.stages;
  if (stages.length === 0) {
    errors.push("An approval chain needs at least one stage.");
    return errors;
  }
  if (stages.length > MAX_STAGES) errors.push(`An approval chain can have at most ${MAX_STAGES} stages.`);

  stages.forEach((s, i) => {
    const label = `Stage ${i + 1}`;
    if (s.role === "BuyerSideSigner")
      errors.push(`${label}: co-borrowers and guarantors are added to the Buyer stage, not as a stage of their own.`);
    if (!s.caption.trim()) errors.push(`${label} needs a caption.`);
    else if (s.caption.length > MAX_CAPTION_LENGTH)
      errors.push(`${label}'s caption is longer than ${MAX_CAPTION_LENGTH} characters.`);
    if (!s.statement.trim()) errors.push(`${label} needs a statement.`);
    else if (s.statement.length > MAX_STATEMENT_LENGTH)
      errors.push(`${label}'s statement is longer than ${MAX_STATEMENT_LENGTH} characters.`);
    if ((s.coSignerCaption?.length ?? 0) > MAX_CAPTION_LENGTH)
      errors.push(`${label}'s co-signer caption is longer than ${MAX_CAPTION_LENGTH} characters.`);
    if ((s.coSignerStatement?.length ?? 0) > MAX_STATEMENT_LENGTH)
      errors.push(`${label}'s co-signer statement is longer than ${MAX_STATEMENT_LENGTH} characters.`);
    const coSigners = s.coSignerPortalUserIds ?? [];
    if (coSigners.length > 0) {
      if (s.role !== "Buyer")
        errors.push(`${label}: only the Buyer stage can carry co-borrowers or guarantors.`);
      if (!s.coSignerCaption?.trim() || !s.coSignerStatement?.trim())
        errors.push(`${label} has co-borrowers or guarantors but no co-signer caption and statement.`);
      if (new Set(coSigners).size !== coSigners.length)
        errors.push(`${label} lists a co-borrower or guarantor account more than once.`);
    }
  });

  const counts = new Map<ApprovalChainRole, number>();
  for (const s of stages) counts.set(s.role, (counts.get(s.role) ?? 0) + 1);
  for (const [role, n] of counts) {
    if (n > 1) errors.push(`The ${roleLabel(role)} stage appears more than once.`);
  }

  if (isBorrowerFacing) {
    const broker = stages.findIndex((s) => s.role === "Broker");
    const seller = stages.findIndex((s) => s.role === "Seller");
    const buyer = stages.findIndex((s) => s.role === "Buyer");
    if (broker < 0 || seller < 0) {
      errors.push(
        "A borrower-facing document must keep a Licensed Intermediary stage and then a Note Holder stage.",
      );
    } else {
      if (broker > seller)
        errors.push(
          "On a borrower-facing document the Licensed Intermediary stage must come before the Note Holder stage.",
        );
      if (!stages[broker].mandatory || !stages[seller].mandatory)
        errors.push(
          "On a borrower-facing document the Licensed Intermediary and Note Holder stages are mandatory.",
        );
      if (buyer >= 0 && (buyer < broker || buyer < seller))
        errors.push(
          "On a borrower-facing document the Licensed Intermediary and Note Holder stages must come before the Borrower stage.",
        );
    }
  }

  baseline?.stages.forEach((b, i) => {
    if (!b.mandatory) return;
    const kept = i < stages.length && stages[i].role === b.role && stages[i].mandatory;
    if (!kept)
      errors.push(
        `The ${roleLabel(b.role)} stage is mandatory: it cannot be removed, moved from position ${i + 1}, or made optional.`,
      );
  });

  return errors;
}

/**
 * Appendix C signature-page wording — the CLIENT MIRROR of
 * PhoenixCapital.Core/Constants/ApprovalChainDefaults.cs (pinned to the C#
 * source, string for string, by approvalChainRules.test.ts).
 */
export const APPENDIX_C = {
  brokerCaption: "Licensed Intermediary — Endorsement",
  brokerStatementBorrowerFacing:
    "I am the Licensed Intermediary for this transaction. I endorse this document version for forwarding to the Borrower’s portal once the Note Holder issues it.",
  brokerStatementSellerLetter:
    "I am the Licensed Intermediary for this transaction. I endorse this document version for forwarding to the Note Holder’s portal.",
  sellerIssuedCaption: "Note Holder — Issued",
  sellerIssuedStatement:
    "I issue this conditional approval in the exercise of my independent authority as Note Holder.",
  sellerApprovedCaption: "Note Holder — Approved",
  sellerApprovedStatement:
    "I confirm the terms above and conditionally approve financing the Buyer Note on them, in the exercise of my independent authority as Note Holder.",
  buyerCaption: "Borrower — Acknowledged and Accepted",
  buyerStatement:
    "I confirm that the information I submitted is accurate and complete, and I agree to proceed on the conditions stated.",
  coSignerCaption: "Co-Borrower / Guarantor — Acknowledged",
  coSignerStatement: "I acknowledge this document and the obligations the Loan Documents will place on me.",
  phoenixCaption: "Phoenix Capital Solutions, LLC — Received, as Note Administrator",
  phoenixStatement: "Received.",
} as const;

type StageWording = Pick<
  ApprovalChainStageDefinition,
  "caption" | "statement" | "coSignerCaption" | "coSignerStatement"
>;

/** The Appendix C wording for a role (server: ApprovalChainRules.CanonicalWording). */
export function canonicalWording(role: ApprovalChainRole, isBorrowerFacing: boolean): StageWording {
  switch (role) {
    case "Broker":
      return {
        caption: APPENDIX_C.brokerCaption,
        statement: isBorrowerFacing
          ? APPENDIX_C.brokerStatementBorrowerFacing
          : APPENDIX_C.brokerStatementSellerLetter,
        coSignerCaption: null,
        coSignerStatement: null,
      };
    case "Seller":
      return isBorrowerFacing
        ? { caption: APPENDIX_C.sellerIssuedCaption, statement: APPENDIX_C.sellerIssuedStatement, coSignerCaption: null, coSignerStatement: null }
        : { caption: APPENDIX_C.sellerApprovedCaption, statement: APPENDIX_C.sellerApprovedStatement, coSignerCaption: null, coSignerStatement: null };
    case "Buyer":
      return {
        caption: APPENDIX_C.buyerCaption,
        statement: APPENDIX_C.buyerStatement,
        coSignerCaption: APPENDIX_C.coSignerCaption,
        coSignerStatement: APPENDIX_C.coSignerStatement,
      };
    case "Phoenix":
      return { caption: APPENDIX_C.phoenixCaption, statement: APPENDIX_C.phoenixStatement, coSignerCaption: null, coSignerStatement: null };
    default:
      return { caption: "", statement: "", coSignerCaption: null, coSignerStatement: null };
  }
}

/**
 * The wording the server will store and print for a per-send stage of this
 * role (server: ApprovalChainRules.ApplyTemplateWording — rulings D2/Q5/Q12:
 * wording is template-level, never taken from the composer): the template
 * default's stage of the same role, else Appendix C. A Buyer stage whose
 * template stage has no co-signer wording takes Appendix C's for co-signers.
 */
export function templateWordingFor(
  role: ApprovalChainRole,
  templateDefault: ApprovalChainDefinition | null | undefined,
  isBorrowerFacing: boolean,
): StageWording {
  const fromTemplate = templateDefault?.stages.find((s) => s.role === role);
  const canonical = canonicalWording(role, isBorrowerFacing);
  const pick = (value: string | null | undefined, fallback: string | null | undefined) =>
    value && value.trim() ? value : (fallback ?? null);
  return {
    caption: fromTemplate?.caption ?? canonical.caption,
    statement: fromTemplate?.statement ?? canonical.statement,
    coSignerCaption: role === "Buyer" ? pick(fromTemplate?.coSignerCaption, canonical.coSignerCaption) : null,
    coSignerStatement: role === "Buyer" ? pick(fromTemplate?.coSignerStatement, canonical.coSignerStatement) : null,
  };
}

/** Canonical JSON for change detection (null-vs-changed request bodies): the
 *  optional members are normalised so an untouched chain compares equal to
 *  the one the server returned. */
export function chainSignature(chain: ApprovalChainDefinition | null | undefined): string {
  if (!chain) return "null";
  return JSON.stringify(
    chain.stages.map((s) => ({
      role: s.role,
      mandatory: s.mandatory,
      caption: s.caption,
      statement: s.statement,
      coSignerCaption: s.coSignerCaption ?? null,
      coSignerStatement: s.coSignerStatement ?? null,
      coSignerPortalUserIds: s.coSignerPortalUserIds?.length ? s.coSignerPortalUserIds : null,
    })),
  );
}
